import { useRef, useState } from 'react';
import type { AdminTarget } from '@dunea/plugin-sdk';
import type { AdminExtensionView } from '@dunea/shared';
import { apiCreateAdminExtensionSession } from '~/lib/api/admin-extensions';
import { LoadError, Spinner } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { panelFrameSrc } from '~/components/workbench/tools/marketplace/marketplace-model';
import {
  PLUGIN_FRAME_SANDBOX,
  usePluginFrameMessages,
} from '~/components/workbench/tools/marketplace/use-plugin-frame-messages';
import { blockHeight } from './admin-extensions-model';

/**
 * One plugin admin extension in a sandboxed iframe on the plugin's origin,
 * opened with a session for (target, resourceId). Same message protocol as
 * plugin panels. Blocks start at the manifest height and may resize themselves
 * (clamped); in a modal the frame fills the dialog and can grow.
 */
export function AdminExtensionFrame({
  appId,
  extension,
  resourceId,
  mode,
  reloadKey = 0,
  onClose,
}: {
  appId: string;
  extension: AdminExtensionView;
  resourceId?: string;
  mode: 'block' | 'modal';
  /** Bump to open a fresh session. */
  reloadKey?: number;
  onClose?: () => void;
}) {
  const target = extension.target as AdminTarget;
  const session = useResource(
    () =>
      apiCreateAdminExtensionSession(appId, extension.installId, {
        target,
        index: extension.index,
        ...(resourceId ? { resourceId } : {}),
      }),
    `${appId}:${extension.installId}:${extension.index}:${resourceId ?? ''}:${reloadKey}`,
  );
  const frame = useRef<HTMLIFrameElement>(null);
  const [requested, setRequested] = useState<number | null>(null);
  const src = session.data ? panelFrameSrc(session.data.url, window.location.origin) : null;

  usePluginFrameMessages(frame, { name: extension.name, onResize: setRequested, onClose });

  if (session.error) {
    return (
      <div className="p-3">
        <LoadError message={`Could not open ${extension.name}.`} onRetry={() => void session.reload()} />
      </div>
    );
  }

  if (session.loading || !session.data) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: mode === 'block' ? blockHeight(extension.height, null) : '100%' }}
      >
        <Spinner className="text-[14px] text-muted" />
      </div>
    );
  }

  if (!src) {
    return <div className="p-3 text-[12px] text-danger">This extension's URL isn't allowed (it must be https).</div>;
  }

  return (
    <iframe
      ref={frame}
      key={src}
      src={src}
      title={`${extension.name}: ${extension.title}`}
      sandbox={PLUGIN_FRAME_SANDBOX}
      referrerPolicy="no-referrer"
      allow="clipboard-write"
      className="block w-full border-0 bg-white"
      style={
        mode === 'block'
          ? { height: `${blockHeight(extension.height, requested)}px` }
          : { height: requested ? `${requested}px` : '100%', minHeight: '100%' }
      }
    />
  );
}
